interface AlertBlock {
  title: string
  content: any
}

interface AlertOptions {
  blocks: AlertBlock[]
}

class AlertService {
  private readonly webhookUrl = process.env.ALERT_SLACK_WEBHOOK_URL
  private readonly envVariables: Record<string, string | undefined>

  constructor() {
    this.envVariables = {
      ...(process.env.VERCEL === '1'
        ? {
            VERCEL_PROJECT_NAME: process.env.VERCEL_PROJECT_NAME,
            VERCEL_URL: process.env.VERCEL_URL,
            VERCEL_PROJECT_PRODUCTION_URL: process.env.VERCEL_PROJECT_PRODUCTION_URL,
            VERCEL_ENV: process.env.VERCEL_ENV
          }
        : {}),
      PROD_DOMAIN_URL: process.env.PROD_DOMAIN_URL,
      ...(process.env.NEXI_API_URL ? { NEXI_API_URL: process.env.NEXI_API_URL } : {}),
      ...(process.env.NEXT_PUBLIC_NEXI_CHECKOUT_URL
        ? { NEXT_PUBLIC_NEXI_CHECKOUT_URL: process.env.NEXT_PUBLIC_NEXI_CHECKOUT_URL }
        : {}),
      ...(process.env.KLARNA_URL ? { KLARNA_URL: process.env.KLARNA_URL } : {}),
      ...(process.env.SVEA_API_CHECKOUT_URL
        ? { SVEA_API_CHECKOUT_URL: process.env.SVEA_API_CHECKOUT_URL }
        : {}),
      ...(process.env.INGRID_API_URL ? { INGRID_API_URL: process.env.INGRID_API_URL } : {}),
      ...(process.env.NSHIFT_CHECKOUT_API_URL
        ? { NSHIFT_CHECKOUT_API_URL: process.env.NSHIFT_CHECKOUT_API_URL }
        : {}),
      ...(process.env.POSTNORD_API_URL ? { POSTNORD_API_URL: process.env.POSTNORD_API_URL } : {})
    }
  }

  async send(message: string, options: AlertOptions): Promise<boolean> {
    // Log to console for Vercel tracking
    console.error(`[ALERT] ${message}`, {
      timestamp: new Date().toISOString(),
      envVariables: this.envVariables,
      blocks: options.blocks.map((block) => ({
        title: block.title,
        content: typeof block.content === 'string' ? block.content : JSON.stringify(block.content)
      }))
    })

    if (!this.webhookUrl) {
      console.warn('No Slack webhook URL configured - alert only logged to console')
      return false
    }

    try {
      const slackBlocks = this.buildSlackBlocks(message, options.blocks)

      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          text: message,
          blocks: slackBlocks
        })
      })

      if (!response.ok) {
        console.error(`Slack webhook failed with status ${response.status}`)
      }

      return response.ok
    } catch (error) {
      console.error('Failed to send Slack alert:', error)
      return false
    }
  }

  private buildSlackBlocks(message: string, blocks: AlertBlock[]): any[] {
    const slackBlocks: any[] = [
      {
        type: 'rich_text',
        elements: [
          {
            type: 'rich_text_section',
            elements: [
              {
                type: 'text',
                text: message,
                style: {
                  bold: true
                }
              }
            ]
          },
          {
            type: 'rich_text_section',
            elements: [
              {
                type: 'text',
                text: 'Timestamp: ',
                style: {
                  bold: true
                }
              },
              {
                type: 'text',
                text: new Date().toISOString()
              }
            ]
          }
        ]
      },
      {
        type: 'rich_text',
        elements: [
          {
            type: 'rich_text_section',
            elements: [
              {
                type: 'text',
                text: 'ENV variables:',
                style: {
                  bold: true
                }
              }
            ]
          },
          {
            type: 'rich_text_preformatted',
            elements: [
              {
                type: 'text',
                text: JSON.stringify(this.envVariables, null, 2)
              }
            ],
            border: 0
          }
        ]
      }
    ]

    for (const block of blocks) {
      // Add title section
      slackBlocks.push({
        type: 'rich_text',
        elements: [
          {
            type: 'rich_text_section',
            elements: [
              {
                type: 'text',
                text: block.title,
                style: {
                  bold: true
                }
              }
            ]
          }
        ]
      })

      // Process content based on type
      if (typeof block.content === 'string') {
        // String content - use rich_text
        slackBlocks.push({
          type: 'rich_text',
          elements: [
            {
              type: 'rich_text_section',
              elements: [
                {
                  type: 'text',
                  text: block.content
                }
              ]
            }
          ]
        })
      } else {
        // Object content - use rich_text_preformatted with chunking
        const jsonStr = JSON.stringify(block.content, null, 2)
        const chunks = this.chunkString(jsonStr, 12000)

        for (const chunk of chunks) {
          slackBlocks.push({
            type: 'rich_text',
            elements: [
              {
                type: 'rich_text_preformatted',
                elements: [
                  {
                    type: 'text',
                    text: chunk
                  }
                ],
                border: 0
              }
            ]
          })
        }
      }
    }

    return slackBlocks
  }

  private chunkString(str: string, chunkSize: number): string[] {
    const chunks: string[] = []

    for (let i = 0; i < str.length; i += chunkSize) {
      chunks.push(str.substring(i, i + chunkSize))
    }

    return chunks
  }
}

// Singleton instance
export const alertService = new AlertService()

// Export for testing
export { AlertService }
